import {
  meetingDetailSchema,
  meetingPageSchema,
  meetingQueueSchema,
  queueEntrySchema,
  taskSummarySchema,
  type ActionItemInput,
  type AddAgendaItemsRequest,
  type CancelMeetingRequest,
  type CreateMeetingRequest,
  type GenerateAgendaRequest,
  type MeetingView,
  type RecordAgendaOutcomeRequest,
  type ReorderAgendaRequest,
  type RequestAgendaItemRequest,
  type SaveMinutesRequest,
  type ScheduleMeetingRequest,
  type SetAttendanceRequest,
  type UpdateMeetingRequest,
  type WithdrawAgendaRequest,
} from '@mfos/shared';
import { useInfiniteQuery, useQuery } from '@tanstack/react-query';
import { apiGet, apiPost, apiPostNoContent, apiPut, withQuery } from '../../lib/api-client';
import { workspaceKey, workspacePath } from '../workspace/workspace.api';
import { useWorkspaceMutation } from '../workspace/workspace-mutation';

const MEETING_PAGE_SIZE = 25;

export const meetingsKey = (workspaceId: string) =>
  [...workspaceKey(workspaceId), 'meetings'] as const;
export const meetingKey = (workspaceId: string, meetingId: string) =>
  [...meetingsKey(workspaceId), 'detail', meetingId] as const;

export function useMeetingQueue(workspaceId: string, enabled = true) {
  return useQuery({
    queryKey: [...meetingsKey(workspaceId), 'queue'] as const,
    queryFn: ({ signal }) =>
      apiGet(workspacePath(workspaceId, 'agenda-items', 'queue'), {
        schema: meetingQueueSchema,
        signal,
      }),
    enabled,
  });
}

export function useMeetings(workspaceId: string, view: MeetingView, enabled = true) {
  return useInfiniteQuery({
    queryKey: [...meetingsKey(workspaceId), 'list', view] as const,
    initialPageParam: undefined as string | undefined,
    queryFn: ({ pageParam, signal }) =>
      apiGet(
        withQuery(workspacePath(workspaceId, 'meetings'), {
          view,
          cursor: pageParam,
          limit: MEETING_PAGE_SIZE,
        }),
        { schema: meetingPageSchema, signal },
      ),
    getNextPageParam: (lastPage) => lastPage.nextCursor ?? undefined,
    enabled,
  });
}

export function useMeeting(workspaceId: string, meetingId: string, enabled = true) {
  return useQuery({
    queryKey: meetingKey(workspaceId, meetingId),
    queryFn: ({ signal }) =>
      apiGet(workspacePath(workspaceId, 'meetings', meetingId), {
        schema: meetingDetailSchema,
        signal,
      }),
    enabled,
  });
}

export function useRequestAgendaItem(workspaceId: string) {
  return useWorkspaceMutation(workspaceId, (body: RequestAgendaItemRequest) =>
    apiPost(workspacePath(workspaceId, 'agenda-items'), body, queueEntrySchema),
  );
}

export function useWithdrawAgendaRequest(workspaceId: string) {
  return useWorkspaceMutation(
    workspaceId,
    ({ requestId, body }: { requestId: string; body: WithdrawAgendaRequest }) =>
      apiPostNoContent(workspacePath(workspaceId, 'agenda-items', requestId, 'withdraw'), body),
  );
}

export function useCreateMeeting(workspaceId: string) {
  return useWorkspaceMutation(workspaceId, (body: CreateMeetingRequest) =>
    apiPost(workspacePath(workspaceId, 'meetings'), body, meetingDetailSchema),
  );
}

/** Every meeting mutation answers with the refreshed meeting detail (task creation excepted). */
export function useMeetingMutations(workspaceId: string, meetingId: string) {
  const path = (...segments: string[]) =>
    workspacePath(workspaceId, 'meetings', meetingId, ...segments);
  return {
    update: useWorkspaceMutation(workspaceId, (body: UpdateMeetingRequest) =>
      apiPut(path(), body, meetingDetailSchema),
    ),
    schedule: useWorkspaceMutation(workspaceId, (body: ScheduleMeetingRequest) =>
      apiPost(path('schedule'), body, meetingDetailSchema),
    ),
    start: useWorkspaceMutation(workspaceId, (body: { version: number }) =>
      apiPost(path('start'), body, meetingDetailSchema),
    ),
    complete: useWorkspaceMutation(workspaceId, (body: { version: number }) =>
      apiPost(path('complete'), body, meetingDetailSchema),
    ),
    cancel: useWorkspaceMutation(workspaceId, (body: CancelMeetingRequest) =>
      apiPost(path('cancel'), body, meetingDetailSchema),
    ),
    setAttendance: useWorkspaceMutation(workspaceId, (body: SetAttendanceRequest) =>
      apiPut(path('attendance'), body, meetingDetailSchema),
    ),
    saveMinutes: useWorkspaceMutation(workspaceId, (body: SaveMinutesRequest) =>
      apiPut(path('minutes'), body, meetingDetailSchema),
    ),
    finalizeMinutes: useWorkspaceMutation(workspaceId, (body: { version: number }) =>
      apiPost(path('minutes', 'finalize'), body, meetingDetailSchema),
    ),
    addAgenda: useWorkspaceMutation(workspaceId, (body: AddAgendaItemsRequest) =>
      apiPost(path('agenda'), body, meetingDetailSchema),
    ),
    generateAgenda: useWorkspaceMutation(workspaceId, (body: GenerateAgendaRequest) =>
      apiPost(path('agenda', 'generate'), body, meetingDetailSchema),
    ),
    reorderAgenda: useWorkspaceMutation(workspaceId, (body: ReorderAgendaRequest) =>
      apiPut(path('agenda', 'order'), body, meetingDetailSchema),
    ),
    removeAgendaItem: useWorkspaceMutation(
      workspaceId,
      ({ itemId, version }: { itemId: string; version: number }) =>
        apiPost(path('agenda', itemId, 'remove'), { version }, meetingDetailSchema),
    ),
    recordOutcome: useWorkspaceMutation(
      workspaceId,
      ({ itemId, body }: { itemId: string; body: RecordAgendaOutcomeRequest }) =>
        apiPost(path('agenda', itemId, 'outcome'), body, meetingDetailSchema),
    ),
    createTask: useWorkspaceMutation(
      workspaceId,
      ({ itemId, body }: { itemId: string; body: ActionItemInput }) =>
        apiPost(path('agenda', itemId, 'tasks'), body, taskSummarySchema),
    ),
  };
}

export type MeetingMutations = ReturnType<typeof useMeetingMutations>;
